import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { AppIcon } from '@/components/AppIcon';
import { SearchSelect } from '@/components/SearchSelect';
import { StorePicker } from '@/components/StorePicker';
import {
  DialogSubject,
  ErrorNote,
  Field,
  FormActions,
  Modal,
  errorMessage,
  quantity,
  todayIso,
} from '@/components/ui';
import { useItemsById, useSkuCatalog, useStockInvalidation, useStores } from '@/hooks/inventory';
import { PackedQtyInput } from '@/components/PackedQtyInput';
import { KEPT_ON_THIS_PC, saveDocument } from '@/hooks/stockSend';
import { useFarmScopedStore } from '@/hooks/useFarmScopedStore';

/** The one item in one store a demand asks more of. */
export interface DemandSubject {
  storeId: string;
  storeCode: string;
  store: string;
  skuId: string;
  skuCode: string;
  skuName: string;
  skuNameUr?: string | null;
  uom: string;
  uomPrecision: number;
  /** What the store holds now. */
  qty: string;
  belowThreshold?: boolean;
}

/**
 * Asks for more of one item in one store. It moves no stock: someone else
 * approves or rejects it on the Stock Demands screen, and marks it fulfilled
 * once the stock has come in by a receipt or a transfer.
 *
 * Opened from a Stock row, the row is the subject and nothing about it is
 * asked again. Opened with no subject, the store and item are chosen here.
 */
export function DemandDialog({
  subject,
  onClose,
  onSaved,
}: {
  subject?: DemandSubject | undefined;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const invalidate = useStockInvalidation();
  const { data: stores } = useStores();
  const [storeId, setStoreId] = useState(subject?.storeId ?? '');
  const [skuId, setSkuId] = useState(subject?.skuId ?? '');
  const [skuLabel, setSkuLabel] = useState('');
  const [qty, setQty] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const catalog = useSkuCatalog(storeId || undefined);
  const { options: storeOptions } = useFarmScopedStore(stores, storeId, (id) => {
    if (!subject) setStoreId(id);
  });
  const uom = subject?.uom ?? catalog.known.get(skuId)?.uom;
  // The item's packages, so "4 Cartons" can be asked for.
  const itemsById = useItemsById();
  const packed = catalog.known.get(skuId) ?? itemsById.get(skuId);

  const save = useMutation({
    // Through the outbox (.docs/023): answered at once with a line, kept on
    // this PC and sent by itself without one.
    mutationFn: () =>
      saveDocument({
        kind: 'demand',
        path: '/api/v1/inventory/demands',
        body: {
          storeId,
          skuId,
          ...(qty ? { qty } : {}),
          ...(neededBy ? { neededBy } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
        post: false,
        describes: `Stock demand: ${qty ? `${qty} ` : ''}${subject?.skuCode ?? catalog.known.get(skuId)?.code ?? 'an item'}`,
        screen: 'inv-demand',
      }),
    onSuccess: (result) => {
      invalidate();
      onSaved(
        result.waiting
          ? KEPT_ON_THIS_PC
          : `${result.documentNo} sent. It is waiting for approval on the Stock Demands screen.`,
      );
    },
  });

  function submit() {
    if (!storeId) return setProblem('Choose the store that needs the stock.');
    if (!skuId) return setProblem('Choose the item that is needed.');
    if (qty && !(Number(qty) > 0)) return setProblem('Enter a quantity above zero, or leave it empty.');
    if (neededBy && neededBy < todayIso()) return setProblem('The date it is needed by cannot be in the past.');
    setProblem(null);
    save.mutate();
  }

  return (
    <Modal title="Demand stock" onClose={onClose}>
      <h2 className="withicon">
        <AppIcon kind="demand" size={18} />
        Demand stock
      </h2>
      {subject && (
        <DialogSubject
          icon="item"
          code={subject.skuCode}
          name={subject.skuName}
          nameUr={subject.skuNameUr ?? null}
          tags={[{ icon: 'store', label: `${subject.storeCode} · ${subject.store}` }]}
          figure={{
            label: 'On hand now',
            value: quantity(subject.qty, subject.uomPrecision),
            unit: subject.uom,
            flag: subject.belowThreshold && (
              <span className="pill pill--alert">
                <AppIcon kind="attention" size={12} />
                Low stock
              </span>
            ),
          }}
        />
      )}

      <div className="stack">
        {!subject && (
          <>
            <StorePicker label="Store" required value={storeId} onChange={setStoreId} stores={stores && storeOptions} />
            <SearchSelect
              label="Item"
              required
              value={skuId}
              selectedLabel={skuLabel || undefined}
              search={catalog.search}
              placeholder="Search items by code or name…"
              onChange={(option) => {
                setSkuId(option?.value ?? '');
                setSkuLabel(option?.label ?? '');
              }}
            />
          </>
        )}
        <div className="formgrid formgrid--2">
          <PackedQtyInput
            label={uom ? `Quantity needed (${uom})` : 'Quantity needed'}
            item={packed}
            itemKey={skuId}
            startWith="package"
            value={qty}
            onChange={setQty}
            hint="Optional — leave empty if not sure"
          />
          <Field label="Needed by" type="date" value={neededBy} onChange={setNeededBy} hint="Leave empty for as soon as possible" />
        </div>
        <Field label="Note" value={note} onChange={setNote} placeholder="e.g. two flocks start on finisher feed next week" />

        {problem && <ErrorNote message={problem} />}
        {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not send the demand')} />}

        <FormActions onCancel={onClose}>
          <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={submit}>
            {save.isPending ? 'Sending…' : 'Send demand'}
          </button>
        </FormActions>
      </div>
    </Modal>
  );
}
